import { zonedDate, zonedWallClock } from "../time-zone-rules";

export type SpotLocalityInput = {
  timezone?: string | null;
  longitude?: number | null;
};

export type SpotLocalContext = {
  date: string;
  hour: number;
  timeZone: string;
  source: "iana" | "bounded-longitude";
};

export type SpotDateLocation = {
  longitude: number;
  timezone?: string | null;
};

export type SpotLocalDateWindow = {
  yesterday: string;
  today: string;
  tomorrow: string;
  forecastEnd: string;
};

function boundedLongitudeOffsetHours(longitude: number): number {
  return Math.max(-12, Math.min(14, Math.round(longitude / 15)));
}

function dateFromShiftedUtc(value: Date): string {
  return `${value.getUTCFullYear()}-${String(value.getUTCMonth() + 1).padStart(2, "0")}-${String(value.getUTCDate()).padStart(2, "0")}`;
}

/** Bounded longitude fallback used only when no canonical local date exists. */
export function boundedSpotLocalDate(now: Date, longitude: number): string {
  const offsetHours = boundedLongitudeOffsetHours(longitude);
  return dateFromShiftedUtc(
    new Date(now.getTime() + offsetHours * 60 * 60 * 1000),
  );
}

// One formatter per IANA zone: constructing Intl.DateTimeFormat costs about 30 microseconds, and
// the freshness rule (score-freshness.ts) asks for many local dates per row. A malformed zone
// throws in the constructor and is never stored.
const LOCAL_DATE_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function localDateFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = LOCAL_DATE_FORMATTERS.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    LOCAL_DATE_FORMATTERS.set(timeZone, formatter);
  }
  return formatter;
}

/** Whether `timeZone` is an IANA zone this runtime can use (else spotLocalDate falls back). */
export function isUsableTimeZone(timeZone: string | null | undefined): boolean {
  if (!timeZone) return false;
  try {
    localDateFormatter(timeZone);
    return true;
  } catch {
    return false;
  }
}

/** Spot-local calendar date from authoritative IANA data, with a bounded fallback. */
export function spotLocalDate(now: Date, location: SpotDateLocation): string {
  if (location.timezone) {
    try {
      localDateFormatter(location.timezone);
      // tzdata 2026e's changes over the runtime's data (lib/time-zone-rules.ts).
      return zonedDate(now.getTime(), location.timezone);
    } catch {
      // Missing or malformed IANA identifiers use the explicitly bounded fallback.
    }
  }
  return boundedSpotLocalDate(now, location.longitude);
}

/** Shift an ISO calendar date without involving the server or browser timezone. */
export function offsetIsoDate(date: string, days: number): string {
  const shifted = new Date(`${date}T12:00:00Z`);
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return dateFromShiftedUtc(shifted);
}

/** Calendar keys used by a spot detail page and its adjacent-day reads. */
export function spotLocalDateWindow(
  now: Date,
  location: SpotDateLocation,
  forecastDays = 7,
): SpotLocalDateWindow {
  const today = spotLocalDate(now, location);
  return {
    yesterday: offsetIsoDate(today, -1),
    today,
    tomorrow: offsetIsoDate(today, 1),
    forecastEnd: offsetIsoDate(today, forecastDays),
  };
}

export function resolveSpotLocalContext(
  now: Date,
  location: SpotLocalityInput,
): SpotLocalContext | null {
  const longitude = usableLongitude(location.longitude)
    ? location.longitude
    : null;
  if (location.timezone) {
    try {
      const date = spotLocalDate(now, {
        timezone: location.timezone,
        longitude: longitude ?? 0,
      });
      const hour = zonedWallClock(now.getTime(), location.timezone).hour;
      if (Number.isInteger(hour) && hour >= 0 && hour <= 23) {
        return {
          date,
          hour,
          timeZone: location.timezone,
          source: "iana",
        };
      }
    } catch {
      // Invalid IANA identifiers may use the bounded longitude fallback below.
    }
  }
  if (longitude === null) return null;

  const offsetHours = boundedLongitudeOffsetHours(longitude);
  const shifted = new Date(now.getTime() + offsetHours * 60 * 60 * 1000);
  return {
    date: spotLocalDate(now, { timezone: null, longitude }),
    hour: shifted.getUTCHours(),
    timeZone: fixedOffsetTimeZone(offsetHours),
    source: "bounded-longitude",
  };
}

function usableLongitude(value: number | null | undefined): value is number {
  return typeof value === "number" &&
    Number.isFinite(value) &&
    value >= -180 &&
    value <= 180;
}

function fixedOffsetTimeZone(offsetHours: number): string {
  if (offsetHours === 0) return "Etc/UTC";
  return offsetHours > 0
    ? `Etc/GMT-${offsetHours}`
    : `Etc/GMT+${Math.abs(offsetHours)}`;
}
